import React, { useState, useMemo } from 'react';
import { Space, Button, Tag, Tooltip, Popconfirm, Modal, Select, message } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, UserSwitchOutlined } from '@ant-design/icons';
import type { Key } from 'react';
import dayjs from 'dayjs';
import { Case, CaseStatus } from '../types';
import { batchUpdateResponsibleLawyer } from '../../../services/case';
import { getStaff } from '../../../services/user';
import { getCurrentUser } from '../../../services/auth';
import { UserRole } from '../../../constants/user';
import EnhancedTableWrapper from '../../../components/EnhancedTableWrapper';
import { caseDefaultColumns } from './caseColumns.config';

const { Option } = Select;

interface CaseListViewEnhancedProps {
  cases: Case[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  onView: (caseItem: Case) => void;
  onEdit: (caseItem: Case) => void;
  onDelete: (id: number) => void;
  onRefresh?: () => void;
}

const CaseListViewEnhanced: React.FC<CaseListViewEnhancedProps> = ({
  cases,
  loading,
  pagination,
  onView,
  onEdit,
  onDelete,
  onRefresh,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState<number | undefined>();
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);

  React.useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (error) {
        console.error('Failed to load current user', error);
      }
    };
    loadCurrentUser();
  }, []);

  const getStatusColor = (status: CaseStatus) => {
    const colors: Record<CaseStatus, string> = {
      [CaseStatus.ACTIVE]: 'processing',
      [CaseStatus.CLOSED]: 'default',
      [CaseStatus.SUSPENDED]: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: CaseStatus) => {
    const texts: Record<CaseStatus, string> = {
      [CaseStatus.ACTIVE]: '进行中',
      [CaseStatus.CLOSED]: '已结案',
      [CaseStatus.SUSPENDED]: '已暂停',
    };
    return texts[status] || status;
  };

  const columnRenderMap: Record<string, any> = useMemo(() => ({
    index: {
      render: (_: any, __: any, index: number) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    case_name: {
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
      ),
    },
    case_reason: {
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>{text || '-'}</Tooltip>
      ),
    },
    case_level: {
      render: (text: string) => text || '-',
    },
    case_status: {
      render: (status: CaseStatus) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    responsible_lawyer_name: {
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>{text || '-'}</Tooltip>
      ),
    },
    assistant_lawyer_name: {
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>{text || '-'}</Tooltip>
      ),
    },
    accept_date: {
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    court_date: {
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    action: {
      render: (_: any, record: Case) => (
        <Space size="small">
          <Tooltip title="查看">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Tooltip>
          <Popconfirm title="确定要删除这个案件吗？" onConfirm={() => onDelete(record.id)} okText="确定" cancelText="取消">
            <Tooltip title="删除">
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  }), [pagination, onView, onEdit, onDelete]);

  const handleBatchUpdateLawyer = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要修改的案件');
      return;
    }
    try {
      const users = await getStaff({ limit: 1000 });
      setLawyers(users);
      setAssignModalVisible(true);
    } catch (error) {
      message.error('加载律师列表失败');
    }
  };

  const confirmBatchUpdate = async () => {
    if (!selectedLawyerId) {
      message.warning('请选择负责律师');
      return;
    }
    setBatchLoading(true);
    try {
      const result = await batchUpdateResponsibleLawyer(selectedRowKeys as number[], selectedLawyerId);
      message.success(`成功修改 ${result.success_count} 个案件的负责律师`);
      setSelectedRowKeys([]);
      setAssignModalVisible(false);
      setSelectedLawyerId(undefined);
      onRefresh?.();
    } catch (error) {
      message.error('批量修改失败');
    } finally {
      setBatchLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: Key[]) => setSelectedRowKeys(selectedKeys),
    columnWidth: 48,
  };

  return (
    <>
      {selectedRowKeys.length > 0 && currentUserRole === UserRole.ADMIN && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#e6f7ff', borderRadius: 4 }}>
          <Space>
            <span>已选择 {selectedRowKeys.length} 项</span>
            <Button type="primary" icon={<UserSwitchOutlined />} onClick={handleBatchUpdateLawyer} loading={batchLoading}>
              批量修改负责律师
            </Button>
            <Button onClick={() => setSelectedRowKeys([])}>取消选择</Button>
          </Space>
        </div>
      )}

      <EnhancedTableWrapper
        viewType="case-management"
        defaultColumns={caseDefaultColumns}
        columnRenderMap={columnRenderMap}
        dataSource={cases}
        loading={loading}
        rowKey="id"
        rowSelection={currentUserRole === UserRole.ADMIN ? rowSelection : undefined}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: pagination.onChange,
        }}
        scroll={{ x: 1500, y: 600 }}
        virtual
      />

      <Modal
        title="批量修改负责律师"
        open={assignModalVisible}
        onOk={confirmBatchUpdate}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedLawyerId(undefined);
        }}
        confirmLoading={batchLoading}
      >
        <p>将选中的 {selectedRowKeys.length} 个案件的负责律师修改为：</p>
        <Select
          style={{ width: '100%' }}
          placeholder="请选择负责律师"
          value={selectedLawyerId}
          onChange={setSelectedLawyerId}
          showSearch
          optionFilterProp="children"
        >
          {lawyers.map((lawyer) => (
            <Option key={lawyer.id} value={lawyer.id}>
              {lawyer.real_name || lawyer.username}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default CaseListViewEnhanced;
